import moment from 'moment-timezone';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, inject } from '@angular/core';
import { BehaviorSubject, filter, map, Observable, switchMap, tap } from 'rxjs';
import { SelectOption } from '@nuso/forms/data-access';
import { GraphApiService } from '@nuso/common/data-access-api';
import { isNonNull } from '@nuso/common/util-ts';
import { NotificationService } from '@nuso/common/util-errors';
import { gql } from 'graphql-tag';

const getCurrentCustomerTimezoneQuery = gql`
  query customerById($id: Int!) {
    customerById(id: $id) {
      id
      timezone
    }
  }
`;

@Component({
    selector: 'nuso-timezone-select',
    templateUrl: './timezone-select.component.html',
    styleUrls: ['./timezone-select.component.scss'],
    standalone: false
})
export class TimezoneSelectComponent implements OnInit, OnChanges {
  private graphApi = inject(GraphApiService);
  private notificationService = inject(NotificationService);

  @Output() timezone = new EventEmitter<string>();
  @Input() selectedTimezone: string | undefined;
  @Input() customerId: number;

  timezones$: Observable<SelectOption[]>;
  customerId$ = new BehaviorSubject<number | null>(null);

  ngOnInit() {
    this.timezones$ = this.customerId$.pipe(
      filter(isNonNull),
      switchMap((customerId) => {
        return this.getCurrentCustomerTimezone(
          customerId
        );
      }),
      tap(data => {
        if (!data) {
          this.notificationService.notify('Failed to load timezone data. Please try again.');
        }
      }),
      filter(isNonNull),
      map((customerTz) => {
        const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const customerTimezone = customerTz.timezone;
        const timezones = moment.tz.names().filter((tzname) => {
          return tzname !== userTimezone && tzname !== customerTimezone;
        });

        if (customerTimezone && customerTimezone !== userTimezone) {
          timezones.unshift(customerTimezone);
        }
        if (userTimezone) {
          timezones.unshift(userTimezone);
        }
        if (timezones.length && !this.selectedTimezone) {
          this.selectedTimezone = `${timezones[0]}`;
        }

        this.timezone.emit(this.selectedTimezone)
        return timezones.map((tzname) => {
          let label = tzname;
          if (tzname === userTimezone) {
            label += ' (user)';
          }
          if (tzname === customerTimezone) {
            label += ' (customer)';
          }
          return { label, value: tzname };
        });
      })
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['customerId']) {
      this.customerId$.next(this.customerId);
    }
  }

  onValueChange(timezone: string): void {
    this.timezone.emit(timezone);
  }

  getCurrentCustomerTimezone(id: number) {
    return this.graphApi.watchQuery<{id: number, timezone: string}>({
      fetchPolicy: 'network-only',
      query: getCurrentCustomerTimezoneQuery,
      variables: {
        id
      }
    });
  }
}
